import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { client } from "@/data/client";
import { cn } from "@/lib/utils";
import type { CallLog, CallOutcome } from "@/data/types";

type SortKey = "recent" | "longest" | "sdr";
type OutcomeFilter = "all" | CallOutcome | "appointment";
const PAGE_SIZE = 7;

const OUTCOME_LABEL: Record<CallOutcome, string> = {
  connected: "Conectada",
  answered: "Atendida",
  voicemail: "Caixa postal",
  "no-answer": "Não atendida",
  failed: "Falha",
};

const OUTCOME_CHIP: Record<CallOutcome, string> = {
  connected: "bg-success/15 text-success",
  answered: "bg-chart-3/15 text-chart-3",
  voicemail: "bg-warning/15 text-warning",
  "no-answer": "bg-text-muted/20 text-text-muted",
  failed: "bg-danger/15 text-danger",
};

export function CallDaySection({
  date,
  onClear,
}: {
  date: string | null;
  onClear: () => void;
}) {
  const [sortBy, setSortBy] = useState<SortKey>("recent");
  const [outcomeFilter, setOutcomeFilter] = useState<OutcomeFilter>("all");
  const [sdrFilter, setSdrFilter] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const q = useQuery({
    queryKey: ["call-day", date],
    queryFn: () => client.getCallDayDetail({ date: date! }),
    enabled: !!date,
  });

  useEffect(() => {
    setSortBy("recent");
    setOutcomeFilter("all");
    setSdrFilter("all");
    setSearch("");
    setPage(1);
  }, [date]);

  useEffect(() => {
    setPage(1);
  }, [sortBy, outcomeFilter, sdrFilter, search]);

  const detail = q.data;

  const sdrOptions = useMemo(() => {
    const set = new Set<string>();
    (detail?.calls ?? []).forEach((c) => set.add(c.sdrName));
    return ["all", ...Array.from(set)];
  }, [detail]);

  const visible = useMemo(() => {
    if (!detail) return [];
    const filtered = detail.calls.filter((c) => {
      if (outcomeFilter === "appointment" && !c.nextStepScheduled) return false;
      if (outcomeFilter !== "all" && outcomeFilter !== "appointment" && c.outcome !== outcomeFilter) return false;
      if (sdrFilter !== "all" && c.sdrName !== sdrFilter) return false;
      if (search && !c.contactName.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
    return [...filtered].sort((a, b) => {
      if (sortBy === "longest") return b.durationSeconds - a.durationSeconds;
      if (sortBy === "sdr") return a.sdrName.localeCompare(b.sdrName);
      return b.startedAt.localeCompare(a.startedAt);
    });
  }, [detail, sortBy, outcomeFilter, sdrFilter, search]);

  const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const pageItems = useMemo(
    () => visible.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [visible, page]
  );

  if (!date) {
    return (
      <section className="rounded-lg border border-dashed border-border-subtle bg-bg-card/50 p-8 text-center">
        <div className="mx-auto max-w-md space-y-1.5">
          <div className="text-sm font-medium text-text-secondary">Ligações do período</div>
          <p className="text-[13px] text-text-muted">
            Clique em qualquer dia do gráfico acima para ver todas as ligações realizadas nesse dia — contato,
            SDR responsável, duração, resultado e se gerou agendamento.
          </p>
        </div>
      </section>
    );
  }

  const dateLabel = new Date(date).toLocaleDateString("pt-BR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  return (
    <section className="overflow-hidden rounded-lg border border-border-subtle bg-bg-card">
      <header className="flex items-start justify-between gap-4 border-b border-border-subtle px-6 py-5">
        <div className="space-y-1">
          <div className="text-[11px] font-medium uppercase tracking-[0.15em] text-brand-amber">
            Ligações do período
          </div>
          <h2 className="text-lg font-semibold capitalize tracking-tight text-text-primary">{dateLabel}</h2>
          {detail && (
            <p className="text-[13px] text-text-muted">
              <span className="font-semibold text-text-primary">{detail.totalCalls}</span> ligações ·
              <span className="ml-1 font-semibold text-chart-3">{detail.answeredCalls}</span> atendidas ·
              <span className="ml-1 font-semibold text-success">{detail.connections}</span> conexões ·
              <span className="ml-1 font-semibold text-brand-amber">{detail.appointmentsGenerated}</span> agendamentos ·
              duração média <span className="font-semibold text-text-primary">{formatDuration(detail.avgDurationSeconds)}</span>
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onClear}
          className="flex items-center gap-1.5 rounded-md border border-border-subtle px-3 py-1.5 text-xs font-medium text-text-muted transition hover:border-border-strong hover:text-text-primary"
        >
          <span>×</span>
          <span>Limpar dia</span>
        </button>
      </header>

      <div className="flex flex-wrap items-center gap-3 border-b border-border-subtle px-6 py-3">
        <input
          type="search"
          placeholder="Buscar por contato…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-9 flex-1 min-w-[200px] rounded-md border border-border-subtle bg-bg-primary px-3 text-sm text-text-primary placeholder:text-text-muted focus:border-brand-amber focus:outline-none"
        />
        <div className="flex items-center gap-2 text-xs text-text-muted">
          <span>Resultado:</span>
          <select
            value={outcomeFilter}
            onChange={(e) => setOutcomeFilter(e.target.value as OutcomeFilter)}
            className="h-9 rounded-md border border-border-subtle bg-bg-primary px-2 text-sm text-text-primary focus:border-brand-amber focus:outline-none"
          >
            <option value="all">Todos</option>
            <option value="connected">Conectadas</option>
            <option value="answered">Atendidas</option>
            <option value="voicemail">Caixa postal</option>
            <option value="no-answer">Não atendidas</option>
            <option value="failed">Falhas</option>
            <option value="appointment">Com agendamento</option>
          </select>
        </div>
        <div className="flex items-center gap-2 text-xs text-text-muted">
          <span>SDR:</span>
          <select
            value={sdrFilter}
            onChange={(e) => setSdrFilter(e.target.value)}
            className="h-9 rounded-md border border-border-subtle bg-bg-primary px-2 text-sm text-text-primary focus:border-brand-amber focus:outline-none"
          >
            {sdrOptions.map((s) => (
              <option key={s} value={s}>{s === "all" ? "Todos" : s}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2 text-xs text-text-muted">
          <span>Ordenar:</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortKey)}
            className="h-9 rounded-md border border-border-subtle bg-bg-primary px-2 text-sm text-text-primary focus:border-brand-amber focus:outline-none"
          >
            <option value="recent">Mais recentes</option>
            <option value="longest">Maior duração</option>
            <option value="sdr">SDR A–Z</option>
          </select>
        </div>
        <div className="ml-auto text-xs text-text-muted">
          {visible.length} de {detail?.totalCalls ?? 0}
        </div>
      </div>

      <div>
        {q.isLoading && (
          <div className="px-6 py-12 text-center text-sm text-text-muted">Carregando ligações…</div>
        )}
        {detail && visible.length === 0 && !q.isLoading && (
          <div className="px-6 py-12 text-center text-sm text-text-muted">
            Nenhuma ligação encontrada com os filtros atuais.
          </div>
        )}
        {visible.length > 0 && (
          <>
            <table className="w-full">
              <thead className="bg-bg-secondary">
                <tr className="text-[11px] font-medium uppercase tracking-[0.1em] text-text-muted">
                  <th className="px-5 py-3 text-left">Contato</th>
                  <th className="px-5 py-3 text-left">SDR</th>
                  <th className="px-5 py-3 text-left">Início</th>
                  <th className="px-5 py-3 text-right">Duração</th>
                  <th className="px-5 py-3 text-left">Resultado</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((call, i) => (
                  <CallRow key={call.callId} call={call} isLast={i === pageItems.length - 1} />
                ))}
              </tbody>
            </table>
            {totalPages > 1 && (
              <Paginator
                page={page}
                totalPages={totalPages}
                onChange={setPage}
                rangeStart={(page - 1) * PAGE_SIZE + 1}
                rangeEnd={Math.min(page * PAGE_SIZE, visible.length)}
                total={visible.length}
              />
            )}
          </>
        )}
      </div>
    </section>
  );
}

function CallRow({ call, isLast }: { call: CallLog; isLast: boolean }) {
  const phoneFmt = call.contactNumber.startsWith("55")
    ? `+55 (${call.contactNumber.slice(2, 4)}) ${call.contactNumber.slice(4, 9)}-${call.contactNumber.slice(9)}`
    : call.contactNumber;
  const startTime = new Date(call.startedAt).toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <tr
      className={cn(
        "text-sm transition hover:bg-bg-card-hover",
        !isLast && "border-b border-border-subtle"
      )}
    >
      <td className="px-5 py-3">
        <div className="flex items-center gap-3">
          <span
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold text-text-on-accent"
            style={{ backgroundColor: call.avatarColor }}
          >
            {getInitials(call.contactName)}
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-text-primary">{call.contactName}</span>
              {call.nextStepScheduled && (
                <span className="inline-flex items-center gap-1 rounded-full bg-brand-amber/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand-amber">
                  📅 Agendou
                </span>
              )}
            </div>
            <div className="text-xs text-text-muted">{phoneFmt}</div>
            <div className="mt-1 flex flex-wrap gap-1">
              {call.tags.map((t) => (
                <span
                  key={t.id}
                  className="inline-flex items-center gap-1 rounded-full bg-bg-primary px-1.5 py-0.5 text-[10px] font-medium text-text-secondary"
                >
                  <span className="h-1 w-1 rounded-full" style={{ backgroundColor: t.color }} />
                  {t.name}
                </span>
              ))}
            </div>
          </div>
        </div>
      </td>
      <td className="px-5 py-3 text-text-secondary">{call.sdrName}</td>
      <td className="px-5 py-3 text-text-secondary">{startTime}</td>
      <td className="px-5 py-3 text-right font-medium text-text-primary">
        {call.durationSeconds > 0 ? formatDuration(call.durationSeconds) : "—"}
      </td>
      <td className="px-5 py-3">
        <span className={cn("inline-flex rounded-full px-2 py-0.5 text-xs font-semibold", OUTCOME_CHIP[call.outcome])}>
          {OUTCOME_LABEL[call.outcome]}
        </span>
      </td>
    </tr>
  );
}

function Paginator({
  page,
  totalPages,
  onChange,
  rangeStart,
  rangeEnd,
  total,
}: {
  page: number;
  totalPages: number;
  onChange: (p: number) => void;
  rangeStart: number;
  rangeEnd: number;
  total: number;
}) {
  return (
    <div className="flex items-center justify-between border-t border-border-subtle px-6 py-3">
      <span className="text-xs text-text-muted">
        Exibindo <span className="font-semibold text-text-primary">{rangeStart}–{rangeEnd}</span> de {total}
      </span>
      <div className="flex items-center gap-1">
        <PagerButton label="‹" disabled={page === 1} onClick={() => onChange(page - 1)} />
        {buildPageWindow(page, totalPages).map((p, i) =>
          p === "…" ? (
            <span key={`ellipsis-${i}`} className="px-2 text-xs text-text-muted">…</span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onChange(p)}
              className={cn(
                "min-w-[30px] rounded-md px-2 py-1 text-xs font-medium transition",
                p === page
                  ? "bg-brand-amber text-text-on-accent"
                  : "text-text-secondary hover:bg-bg-card-hover hover:text-text-primary"
              )}
            >
              {p}
            </button>
          )
        )}
        <PagerButton label="›" disabled={page === totalPages} onClick={() => onChange(page + 1)} />
      </div>
    </div>
  );
}

function PagerButton({ label, disabled, onClick }: { label: string; disabled: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "flex h-7 w-7 items-center justify-center rounded-md text-sm transition",
        disabled
          ? "cursor-not-allowed text-text-muted/40"
          : "text-text-secondary hover:bg-bg-card-hover hover:text-text-primary"
      )}
    >
      {label}
    </button>
  );
}

function buildPageWindow(current: number, total: number): (number | "…")[] {
  if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | "…")[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) pages.push("…");
  for (let p = start; p <= end; p++) pages.push(p);
  if (end < total - 1) pages.push("…");
  pages.push(total);
  return pages;
}

function formatDuration(seconds: number): string {
  if (seconds === 0) return "0s";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m${s.toString().padStart(2, "0")}s` : `${s}s`;
}

function getInitials(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
